import { useEffect, useState } from 'react'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { bg } from 'lib/background'
import {
  getActivityBuiltInTagColor,
  getBuiltInTags,
  getContactBuiltInTagColor,
  getContactBuiltInTags,
} from 'lib/getBuiltInTags'
import { logWarn } from 'lib/logging'
import { run } from 'lib/run'
import { TagChip } from './TagChip'

type TagProps = {
  tag: string
  workspaceId?: string
  model: string
  readOnly?: boolean
  tagClassName?: string
  removeTag: () => void
}

const fetching: Record<string, Promise<string>> = {}
const cache: Record<string, string> = {}

export function setColorCache(tagName: string, model: string, color: string, workspaceId?: string) {
  const key = createKey(model, tagName, workspaceId)
  cache[key] = color
}

export async function getColor(tagName: string, model: string, workspaceId?: string) {
  const key = createKey(model, tagName, workspaceId)
  if (cache[key] != null) return cache[key]
  const builtInColor = getBuiltInColor(tagName, model)
  if (builtInColor) return (cache[key] = builtInColor)
  if (fetching[key] != null) return fetching[key]
  else if (!tagName || !model) return
  let entity: string
  switch (model) {
    case `activities`:
      entity = `Activity`
      break
    case `contacts`:
      entity = `Contact`
      break
    case `calls`:
      entity = `Call`
      break
    case `forms`:
      entity = `Form`
      break
    case `workspaces`:
      entity = `Workspace`
      break
    case `locations`:
      entity = `Location`
      break
    default:
      entity = model
  }
  return (fetching[key] = run(async () => {
    try {
      const searchTerms = [`name.keyword:"${tagName}"`, `entity:${entity}`].join(' AND ')
      const result = await gqlFetchList<{ color: string }>(
        'tagColors',
        { search: searchTerms, pagination: { limit: 1 } },
        'color',
      )
      const color = result.hits[0]?.color || `bg-tags-silver`
      return (cache[key] = color)
    } catch (err) {
      logWarn(`Failed to fetch tag color for ${tagName} ${model}`, err)
      return (cache[key] = `bg-tags-silver`)
    }
  }))
}

const createKey = (model: string, tagName: string, workspaceId?: string) => `${workspaceId}-${model}-${tagName}`

function getBuiltInColor(tagName: string, model: string) {
  if ((model === `activities` || model === `Activity`) && getBuiltInTags().includes(tagName)) {
    return getActivityBuiltInTagColor(tagName)
  }
  if ((model === `contacts` || model === `Contact`) && getContactBuiltInTags().includes(tagName)) {
    return getContactBuiltInTagColor(tagName)
  }
}

export default function Tag({ tag, workspaceId, model, readOnly = false, tagClassName = ``, removeTag }: TagProps) {
  const [color, setColor] = useState<string>(cache[createKey(model, tag, workspaceId)])
  useEffect(() => {
    bg(
      getColor(tag, model, workspaceId).then((color) => {
        if (color) setColor(color)
      }),
    )
  }, [tag, workspaceId, model])
  return (
    <TagChip
      name={tag}
      colorKey={color}
      labelClassName={tagClassName}
      onRemove={!readOnly && !getBuiltInTags().includes(tag) ? removeTag : undefined}
    />
  )
}
